import { useState } from 'react';
import { toast } from 'react-toastify';
import client from '../api/client';
import { messaggioErrore } from '../utils/apiError';
import { Button, DialogBody, DialogFooter, Modal, classeCampo } from './ui';

// «Dati che restituisce»: la tabella di un'operazione, proposta leggendo una
// risposta vera e confermata qui. Con la tabella l'API si usa come il meteo:
// spunte, righe dell'elenco, condizioni (29/09/2026).
const TIPI = [['testo', 'Testo'], ['numero', 'Numero'], ['intero', 'Numero intero'], ['vero_falso', 'Sì/no'], ['da_confermare', 'Da confermare']];

// Le etichette e le scelte già confermate restano: la proposta aggiunge solo i dati nuovi.
export function unisci(proposta, attuale) {
  const tieni = gruppo => {
    const prima = Object.fromEntries((attuale?.[gruppo] || []).map(c => [c.id, c]));
    return (proposta[gruppo] || []).map(c => (prima[c.id] ? { ...c, ...prima[c.id], esempio: c.esempio } : c));
  };
  return { elenco: proposta.elenco, colonne: tieni('colonne'), generali: tieni('generali'), ...(attuale?.ordina ? { ordina: attuale.ordina } : {}) };
}

function Righe({ titolo, nota, campi, onChange }) {
  if (!campi.length) return null;
  const cambia = (i, patch) => onChange(campi.map((c, j) => (j === i ? { ...c, ...patch } : c)));
  return <fieldset className="space-y-2">
    <legend className="font-medium text-sm">{titolo}</legend>
    <p className="text-xs text-muted">{nota}</p>
    {campi.map((campo, i) => <div key={campo.id} className="grid grid-cols-[auto_minmax(0,1fr)_4.5rem] sm:grid-cols-[auto_minmax(0,1fr)_8rem_5rem] gap-2 items-center">
      <input type="checkbox" checked={campo.mostrabile !== false} aria-label={`Si può mostrare: ${campo.etichetta}`}
        onChange={e => cambia(i, { mostrabile: e.target.checked })} />
      <span className="min-w-0">
        <input className={classeCampo} aria-label={`Nome di ${campo.id}`} value={campo.etichetta}
          onChange={e => cambia(i, { etichetta: e.target.value })} />
        <span className="block text-[11px] text-muted truncate">{campo.id}{campo.esempio !== undefined && campo.esempio !== null ? ` · es. ${String(campo.esempio).slice(0, 30)}` : ''}</span>
      </span>
      <select className={`${classeCampo} hidden sm:block`} aria-label={`Tipo di ${campo.etichetta}`} value={campo.tipo}
        onChange={e => cambia(i, { tipo: e.target.value })}>
        {TIPI.map(([valore, etichetta]) => <option key={valore} value={valore}>{etichetta}</option>)}
      </select>
      <input className={classeCampo} aria-label={`Unità di ${campo.etichetta}`} placeholder="unità" value={campo.unita || ''}
        onChange={e => cambia(i, { unita: e.target.value })} />
    </div>)}
  </fieldset>;
}

export default function TabellaDati({ servizio, onChiudi, onSalvata }) {
  const [tabella, setTabella] = useState(servizio.schema?.colonne || servizio.schema?.generali ? servizio.schema : null);
  const [testo, setTesto] = useState('');
  const [inCorso, setInCorso] = useState(false);
  const [avviso, setAvviso] = useState('');
  const usaTesto = (servizio.url_template || '').includes('{{testo}}');

  const leggi = async () => {
    setInCorso(true);
    try {
      const { data } = await client.post(`/v1/external-services/${servizio.id}/schema/proponi/`, { text: testo }, { timeout: 40000 });
      setTabella(unisci(data, tabella));
      setAvviso(data.avviso || '');
    } catch (e) { toast.error(messaggioErrore(e)); }
    finally { setInCorso(false); }
  };
  const salva = async () => {
    setInCorso(true);
    try {
      await client.patch(`/v1/external-services/${servizio.id}/`, { schema: tabella });
      toast.success('Tabella dei dati salvata');
      onSalvata();
    } catch (e) { toast.error(messaggioErrore(e)); }
    finally { setInCorso(false); }
  };

  return <Modal open size="xl" ridimensionabile onClose={onChiudi} label={`Dati che restituisce: ${servizio.name}`}>
    <DialogBody>
      <div className="space-y-4">
        <p className="text-sm text-muted">
          Leggo una risposta vera dell’operazione e ti propongo i dati che contiene. Correggi i nomi se serve e
          togli la spunta a quelli da non mostrare mai: poi le automazioni li useranno come quelli del meteo.
        </p>
        <div className="flex gap-2">
          {usaTesto && <input className={classeCampo} aria-label="Testo per la lettura" placeholder="Es. 7"
            value={testo} onChange={e => setTesto(e.target.value)} />}
          <Button onClick={leggi} loading={inCorso}>{tabella ? 'Rileggi la risposta' : 'Leggi la risposta'}</Button>
        </div>
        {avviso && <p role="alert" className="rounded bg-warning-bg p-2 text-sm text-warning">{avviso}</p>}
        {tabella && <>
          {tabella.elenco && <p className="text-sm">La risposta è un <strong>elenco</strong>{tabella.elenco === '.' ? '' : ` («${tabella.elenco}»)`}: ogni riga ha le colonne qui sotto.</p>}
          {tabella.elenco && (tabella.colonne || []).length > 0 && <label className="flex flex-wrap items-center gap-2 text-sm">Ordina le righe per
            <select className={`${classeCampo} w-auto max-w-full`} value={tabella.ordina || ''}
              onChange={e => setTabella({ ...tabella, ordina: e.target.value || undefined })}>
              <option value="">come arrivano</option>
              {tabella.colonne.map(c => <option key={c.id} value={c.id}>{c.etichetta}</option>)}
            </select>
          </label>}
          <Righe titolo="Colonne dell’elenco" nota="I dati di ogni riga (una squadra, una sfida…)." campi={tabella.colonne || []}
            onChange={colonne => setTabella({ ...tabella, colonne })} />
          <Righe titolo="Dati generali" nota="I dati fuori dall’elenco (la giornata, lo stato…)." campi={tabella.generali || []}
            onChange={generali => setTabella({ ...tabella, generali })} />
        </>}
      </div>
    </DialogBody>
    <DialogFooter>
      <Button onClick={onChiudi}>Chiudi</Button>
      <Button variant="primary" disabled={!tabella || !!avviso} loading={inCorso} onClick={salva}>Salva la tabella</Button>
    </DialogFooter>
  </Modal>;
}
